import { isInternalUrl, isShrunkSidebarUrl } from 'helpers/pageHelpers'
import { STATUS_BAR_STYLE } from 'constants/mobileApp'
import { Remote as WebviewRemote } from 'react-native-webview-messaging/dist/web'
import { Communication as Com, HOMEPAGE_URL } from 'tangle-shared'
import { captureWarning } from 'helpers/errorHelpers'
import { IS_STATUS_APP } from 'helpers/userAgentHelpers'
import { EXTERNAL_PAGE_URLS_SET } from 'constants/pages'
import { API_URL } from 'constants/api'
import { Subject } from 'rxjs'
import { semverGte } from 'helpers/semverHelpers'
import { shareReplay } from 'rxjs/operators'

export class Remote {
  private webviewRemote: WebviewRemote | null = null

  private isPopup = false

  private initSubject = new Subject()
  init$ = this.initSubject.pipe(shareReplay())

  init = (isPopup: boolean, webviewRemote?: WebviewRemote) => {
    this.isPopup = isPopup
    if (webviewRemote) {
      this.webviewRemote = webviewRemote
    }
    this.initSubject.next()
  }

  on <TEvent extends EventType> (eventName: TEvent['name'], handler: (eventData: TEvent['data']) => void) {
    if (!this.webviewRemote) {
      if (IS_STATUS_APP && !this.isAppOpenedInPopup()) {
        captureWarning(`Can't subscribe to remote`, {
          eventName,
        })
      }

      return
    }

    this.webviewRemote.on(eventName, handler)
  }

  off <TEvent extends EventType> (eventName: TEvent['name'], handler: (eventData: TEvent['data']) => void) {
    if (!this.webviewRemote) {
      if (IS_STATUS_APP && !this.isAppOpenedInPopup()) {
        captureWarning(`Can't unsubscribe from remote`, {
          eventName,
        })
      }

      return
    }

    if (this.webviewRemote.off) {
      this.webviewRemote.off(eventName, handler)
    }
  }

  isAppOpenedInPopup () {
    return this.isPopup
  }

  sendReadyEvent = () => {
    this.sendEvent<Com.ReadyEvent>('ready', undefined)
  }

  sendNavigationEndEvent = (url: string, isAuthenticated: boolean, canGoBack: boolean) => {
    this.sendEvent<Com.NavigationEndEvent>('navigation-end', {
      style: getMobileAppStyle(url, isAuthenticated),
      url,
      canGoBack,
    })
  }

  sendLogoutEvent = () => {
    this.sendEvent<Com.LogoutEvent>('logout', undefined)
  }

  sendRegisterEvent = (credentials: Com.Credentials) => {
    this.sendEvent<Com.RegisterEvent>('register', credentials)
  }

  sendPasswordResetEvent = (credentials: Com.Credentials) => {
    this.sendEvent<Com.PasswordResetEvent>('password-reset', credentials)
  }

  sendExternalUrlEvent = (data: Com.ExternalUrlEventData) => {
    this.sendEvent<Com.ExternalUrlEvent>('external-url', data)
  }

  sendInternetUrlEvent = (data: Com.InternetUrlEventData) => {
    this.sendEvent<Com.InternetUrlEvent>('internet-url', data)
  }

  sendNetworkErrorEvent = () => {
    this.sendEvent<Com.NetworkErrorEvent>('network-error', undefined)
  }

  sendReloadEvent = (url?: string) => {
    this.sendEvent<Com.ReloadEvent>('reload', { url })
  }

  sendLogEvent = (...args: any[]) => {
    this.sendEvent<Com.LogEvent>('log', args)
  }

  sendShareEvent = (data: Com.SharedData) => {
    this.sendEvent<Com.ShareEvent>('share', data)
  }

  sendAnalyticsEvent = <TAnalyticsKind extends Com.AnalyticsKind>(kind: TAnalyticsKind, event: Com.AnalyticsEventsMap[TAnalyticsKind]) => {
    this.sendEvent<Com.AnalyticsEvent<TAnalyticsKind>>('analytics', {
      kind,
      event,
    })
  }

  promptPushNotificationsPermissions = () => {
    this.sendEvent<Com.PromptPushNotificationsPermissionsEvent>('prompt-push-notifications-permissions', undefined)
  }

  private sendEvent<TEvent extends EventType> (eventName: TEvent['name'], eventData: TEvent['data']) {
    if (!this.webviewRemote) {
      if (IS_STATUS_APP) {
        captureWarning('remote.sendEvent: remote is not defined')
      }
      return
    }

    console.log('remote.sendEvent', {
      eventName,
      eventData,
    })

    this.webviewRemote.emit<TEvent>(eventName, eventData)
  }
}

export const remote = new Remote()

export function getMobileAppStyle (url: string, isAuthenticated: boolean): Com.NavigationEndEventData['style'] {
  let statusBar = STATUS_BAR_STYLE['external']

  if (url && isShrunkSidebarUrl(url, isAuthenticated)) {
    statusBar = STATUS_BAR_STYLE['shrunkSidebar']
  } else if (!url || isInternalUrl(url)) {
    statusBar = STATUS_BAR_STYLE['internal']
  }

  return {
    headBgColor: statusBar.backgroundColor,
    altHeadBgColor: statusBar.backgroundColor,
    statusbarStyle: statusBar.style,
  }
}

const isExternalUrl = (url: string): boolean => {
  return EXTERNAL_PAGE_URLS_SET.has(url)
}

const cancelEvent = (event: MouseEvent, isPropagationAllowed: boolean) => {
  event.preventDefault()
  if (!isPropagationAllowed) {
    event.stopImmediatePropagation()
  }
}

const handleDocumentAnchorClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null
  if (!target || !target.closest) {
    return
  }

  const closestAnchorNode = target.closest('a')
  if (!closestAnchorNode) {
    return
  }

  const isPropagationAllowed = Boolean(target.closest('[data-allow-propagation]'))
  const href = closestAnchorNode.getAttribute('href')
  if (href === null) {
    return
  } else if (href === HOMEPAGE_URL) {
    cancelEvent(event, isPropagationAllowed)
    return
  }

  let isInternetUrlClicked = false
  let isExternalUrlClicked = false
  try {
    const url = new URL(href)
    if (url.protocol === 'mailto:') {
      remote.sendExternalUrlEvent({ url: href })
      return
    } else if (url.protocol === 'javascript:') {
      return
    } else if (url.origin === location.origin) {
      throw 'The same origin link!'
    } else {
      isInternetUrlClicked = true
    }
  } catch (error) {
    const hrefWithoutOrigin = href.startsWith(location.origin)
      ? href.substr(location.origin.length)
      : href

    if (hrefWithoutOrigin.startsWith(API_URL)) {
      isInternetUrlClicked = true // it's a redirect via API
    } else {
      isExternalUrlClicked = isExternalUrl(hrefWithoutOrigin)
    }
  }

  if (isInternetUrlClicked) {
    cancelEvent(event, isPropagationAllowed)
    remote.sendInternetUrlEvent({ url: href })
  } else if (isExternalUrlClicked) {
    cancelEvent(event, isPropagationAllowed)
    remote.sendExternalUrlEvent({ url: location.origin + href })
  }
}

export function captureNavigationEvents () {
  document.addEventListener('click', handleDocumentAnchorClick, true)
}

// App before version 1.14.0+build.2101030 can't play video in full screen mode on Android
export const shouldPlayVideoInPopup = (isMobile: boolean, isAndroidApp: boolean, appVersion: string | null) => {
  if (!isMobile) {
    return true
  }

  if (!isAndroidApp) {
    return false
  }

  if (!appVersion) {
    return true
  }

  const isAppVerGreater = semverGte(appVersion, '1.14.0')
  if (isAppVerGreater === null) {
    return true
  }
  return !isAppVerGreater
}
